import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, RotateCcw, Clock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "mp-shade-order";

const summarise = (t = {}) => {
  const bits = [];
  if (t.meters) bits.push(`${t.thans} ${t.thans === 1 ? "than" : "thans"} · ${t.meters} m`);
  if (t.packs) bits.push(`${t.packs} pack${t.packs === 1 ? "" : "s"} · ${t.pieces} pieces`);
  return bits.join("  ·  ");
};

const shadeImageUrl = (qualityId, number) =>
  qualityId === "american-crepe"
    ? `/api/shades-static/crepe/${number}.webp`
    : `/api/shades-static/lining/${number}.webp`;

/* Past orders with one-tap reorder. Writes straight into the same localStorage
   key the catalogue tray reads, then sends the buyer to the catalogue. */
export default function MyOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/my-orders`, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();
      setOrders(data.orders || []);
    } catch (e) {
      console.error("Past orders load failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const reorder = (order) => {
    const items = order.items.map((i) => ({
      ...i,
      key: i.cut_length ? `${i.quality_id}|cut|${i.cut_length}` : `${i.quality_id}|${i.number}`,
      image_url: i.number != null ? shadeImageUrl(i.quality_id, i.number) : undefined,
    }));
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.debug("Could not stage the reorder:", e);
    }
    navigate(`/catalogue/${order.items[0]?.quality_id || "600-lining"}`);
  };

  if (loading) {
    return (
      <p
        className="flex items-center gap-2 text-sm"
        style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
        data-testid="my-orders-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your orders&hellip;
      </p>
    );
  }

  if (!orders.length) {
    return (
      <div
        className="border rounded-sm p-8 text-center"
        style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-card)" }}
        data-testid="my-orders-empty"
      >
        <ShoppingBag className="w-5 h-5 mx-auto mb-3" style={{ color: "var(--mp-muted)" }} />
        <p
          className="text-base mb-1"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
        >
          No orders yet
        </p>
        <p className="text-sm" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
          Orders you send from the shade catalogue will appear here, ready to send again.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="my-orders-list">
      {orders.map((order, idx) => (
        <div
          key={order.reference}
          className="border rounded-sm p-4"
          style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-card)" }}
          data-testid={`my-order-card-${idx}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
            <div>
              <p
                className="text-sm font-semibold"
                style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
                data-testid={`my-order-reference-${idx}`}
              >
                {order.reference}
              </p>
              <p
                className="text-xs mt-0.5 inline-flex items-center gap-1"
                style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
              >
                <Clock className="w-3 h-3" />
                {new Date(order.timestamp).toLocaleDateString("en-IN", { dateStyle: "medium" })}
              </p>
            </div>
            <button
              onClick={() => reorder(order)}
              data-testid={`my-order-reorder-${idx}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-sm transition-opacity hover:opacity-90"
              style={{
                backgroundColor: "var(--mp-copper-solid)",
                color: "#FFFFFF",
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              <RotateCcw className="w-3.5 h-3.5" /> Order this again
            </button>
          </div>

          <ul className="space-y-0.5">
            {order.items.map((i, k) => (
              <li
                key={`${i.quality_id}-${i.number ?? i.cut_length}-${k}`}
                className="text-xs"
                style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
              >
                {i.quality_name} ·{" "}
                {i.cut_length
                  ? `cut piece ${i.cut_length} — ${i.thans} pack(s)`
                  : `#${i.number} — ${i.thans} than × ${i.than_meters}m`}
              </li>
            ))}
          </ul>
          <p
            className="text-[11px] mt-2"
            style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
          >
            {summarise(order.totals)}
          </p>
        </div>
      ))}
    </div>
  );
}
